import React, { useState, useEffect } from "react";
import { User, Check, Package } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fetchCustomers, type ClientRecord } from "@/lib/utils";
import { getAuthenticatedUser } from "@/lib/auth";
import type { ProductRecord } from "@/lib/api";

// ----------------------------------------------------------------------------

interface SelectCustomerModalProps {
    isOpen: boolean;
    product: ProductRecord | null;
    onOpenChange: (open: boolean) => void;
    onConfirm: (product: ProductRecord, clientId: string) => void;
}

export function SelectCustomerModal(props: SelectCustomerModalProps) {
    const { isOpen, product, onOpenChange, onConfirm } = props;
    const [clients, setClients] = useState<ClientRecord[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<string>("");
    const [isLoadingClients, setIsLoadingClients] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (!isOpen) return;

        let isMounted = true;
        async function load() {
            setIsLoadingClients(true);
            try {
                const currentUser = getAuthenticatedUser();
                const activeAgent =
                    currentUser?.agentTag ||
                    (currentUser?.agentCode
                        ? `agente-${currentUser.agentCode}`
                        : "");
                const list = await fetchCustomers(activeAgent);
                const tagLower = currentUser?.agentTag?.toLowerCase().trim() || "";
                const codeStr = currentUser?.agentCode?.trim().toLowerCase() || "";

                const nonAgentList = list.filter((c) => {
                    if (c.isAgent) return false;
                    if (!tagLower && !codeStr) return true;
                    const tags = (c.tags ?? []).map((t) => t.toLowerCase().trim());
                    return tags.some((t) => {
                        if (tagLower && t === tagLower) return true;
                        if (
                            codeStr &&
                            (t === `agente-${codeStr}` ||
                                t === `agent-${codeStr}` ||
                                t === codeStr)
                        )
                            return true;
                        return false;
                    });
                });
                if (isMounted) {
                    setClients(nonAgentList);
                    if (nonAgentList.length > 0) {
                        setSelectedClientId(nonAgentList?.[0]?.id || "");
                    }
                }
            } catch (err) {
                console.error(
                    "[SelectCustomerModal] Failed to load clients:",
                    err,
                );
            } finally {
                if (isMounted) setIsLoadingClients(false);
            }
        }
        void load();
        return () => {
            isMounted = false;
        };
    }, [isOpen]);

    const filteredClients = clients.filter((c) =>
        [c.name, c.email, c.id]
            .filter(Boolean)
            .some((val) =>
                val.toLowerCase().includes(searchTerm.toLowerCase()),
            ),
    );

    function handleConfirm() {
        if (!product || !selectedClientId) return;
        onConfirm(product, selectedClientId);
    }

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md p-6">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <User className="h-5 w-5 text-[#0D6EFD]" />
                        Seleziona Cliente per l&apos;Ordine
                    </DialogTitle>
                    <DialogDescription className="text-sm text-gray-500 mt-1">
                        Scegli il cliente a cui assegnare questo nuovo ordine
                        prima di procedere.
                    </DialogDescription>
                </DialogHeader>

                {product && (
                    <div className="my-4 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50/60 p-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700 font-bold text-xs">
                            <Package className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-xs text-blue-600 font-medium">
                                Prodotto selezionato
                            </div>
                            <div className="truncate text-sm font-bold text-gray-900">
                                {product.name || product.code} ({product.code})
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-3">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Cliente B2B
                    </label>
                    <input
                        type="text"
                        placeholder="Cerca cliente per nome o email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs focus:border-[#0D6EFD] focus:outline-none"
                    />

                    <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-gray-100 p-1">
                        {isLoadingClients ? (
                            <div className="p-4 text-center text-xs text-gray-400">
                                Caricamento clienti...
                            </div>
                        ) : filteredClients.length === 0 ? (
                            <div className="p-4 text-center text-xs text-gray-400">
                                Nessun cliente trovato
                            </div>
                        ) : (
                            filteredClients.map((client) => (
                                <button
                                    key={client.id}
                                    type="button"
                                    onClick={() =>
                                        setSelectedClientId(client.id)
                                    }
                                    className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between text-xs transition-colors ${
                                        selectedClientId === client.id
                                            ? "bg-blue-50 text-blue-900 font-semibold"
                                            : "hover:bg-gray-50 text-gray-700"
                                    }`}>
                                    <div className="truncate">
                                        <div className="font-medium text-gray-900">
                                            {client.name}
                                        </div>
                                        <div className="text-[11px] text-gray-500">
                                            {client.email}
                                        </div>
                                    </div>
                                    {selectedClientId === client.id && (
                                        <Check className="h-4 w-4 text-[#0D6EFD] shrink-0" />
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-2 pt-3 border-t border-gray-100">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenChange(false)}>
                        Annulla
                    </Button>
                    <Button
                        size="sm"
                        disabled={!selectedClientId}
                        onClick={handleConfirm}
                        className="bg-[#0D6EFD] hover:bg-blue-700 text-white">
                        Continua all&apos;ordine
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
